import type { ReactElement } from "react";

import { asSentence } from "./sentence.js";

/**
 * The designer's joiner rule (`J1`): **wherever a label of ours introduces the server's words, the screen prints the LABEL, a FULL STOP, then the
 * server's sentence with a capital and its own stop**, « Capacité non calculée. Le revenu net n'est pas retenu : les relevés ne sont pas lus. », the
 * label in the heavier weight and the sentence in the regular one. A label that already ends on a mark keeps it and words that already end on a stop
 * get no second one; the words stay byte-identical apart from the capital and the stop (the one `asSentence`, never a second helper). One helper for
 * every caller, frozen like `sentence.ts`.
 */
const closed = (label: string): string => (/[.!?…]$/u.test(label) ? label : `${label}.`);

/**
 * `stacked` (`#778`, D2 `bay.js:77-79`, `<b>Pas de proposition.</b><p>…</p>`): the lead is the heavier weight on its OWN line and the sentence stands below it, a small step down
 * (D2's `.failbox p { margin-top: 4px }`). The same joiner, the same words: only the layout differs, so the one helper stays the one.
 */
export function LabelledSentence(props: {
  readonly label: string;
  readonly words: string;
  readonly stacked?: boolean;
}): ReactElement {
  const sentence = asSentence(props.words);
  const stacked = props.stacked === true;
  return (
    <>
      <span
        className={stacked ? "block font-semibold" : "font-semibold"}
        {...(stacked ? { "data-bay": "lead" } : {})}
      >
        {closed(props.label)}
      </span>
      {sentence === "" ? null : (
        <>
          {" "}
          <span
            className={stacked ? "mt-1 block font-normal" : "font-normal"}
            {...(stacked ? { "data-bay": "words" } : {})}
          >
            {sentence}
          </span>
        </>
      )}
    </>
  );
}
